import React, { useState, useEffect, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { QrCode, ShieldCheck, ShieldAlert, ShieldQuestion, RefreshCw, Copy, Play, Monitor, Layers, Link2, Scan } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SURFACES = [
  { id: 'fullscreen', label: 'Full-screen ad', hint: 'Corner card over the spot', icon: Play },
  { id: 'holding', label: 'Holding screen', hint: 'Biggest scan window', icon: Monitor },
  { id: 'lower-third', label: 'Lower third', hint: 'Inside the banner', icon: Layers },
];

const VERDICTS = {
  verified: { label: 'Verified', cls: 'text-emerald-400 border-emerald-500/25 bg-emerald-500/5', Icon: ShieldCheck },
  flagged: { label: 'Flagged', cls: 'text-red-400 border-red-500/25 bg-red-500/5', Icon: ShieldAlert },
  unreachable: { label: 'Unreachable', cls: 'text-yellow-400 border-yellow-500/25 bg-yellow-500/5', Icon: ShieldAlert },
  pending: { label: 'Review pending', cls: 'text-gray-400 border-white/10 bg-white/[0.02]', Icon: ShieldQuestion },
};

export const QrLinkDialog = ({ ad, open, onClose, API, token, onSaved, isAdmin = false }) => {
  const headers = { Authorization: `Bearer ${token}` };
  const [url, setUrl] = useState('');
  const [caption, setCaption] = useState('');
  const [placements, setPlacements] = useState(['fullscreen', 'holding']);
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [current, setCurrent] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!ad) return;
    setUrl(ad.qrUrl || '');
    setCaption(ad.qrCaption || '');
    setPlacements(ad.qrPlacements?.length ? ad.qrPlacements : ['fullscreen', 'holding']);
    setEnabled(ad.qrEnabled !== false);
    setCurrent(ad.qrShortCode ? { qrShortCode: ad.qrShortCode, qrStatus: ad.qrStatus, qrCheck: ad.qrCheck, qrOverride: ad.qrOverride } : null);
  }, [ad]);

  const loadStats = useCallback(async () => {
    if (!ad?.id) return;
    try { const r = await axios.get(`${API}/ads/${ad.id}/qr-stats`, { headers }); setStats(r.data); } catch { /* no stats yet */ }
  }, [ad?.id, API, token]);

  useEffect(() => { if (open) loadStats(); }, [open, loadStats]);

  const trackedUrl = current?.qrShortCode ? `${BACKEND_URL}/api/r/${current.qrShortCode}` : null;
  const previewValue = trackedUrl || (url ? (url.startsWith('http') ? url : `https://${url}`) : 'https://ayo.ads');

  const togglePlacement = (id) => setPlacements(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const save = async () => {
    if (!url.trim()) { toast.error('Add a destination URL first'); return; }
    if (!placements.length) { toast.error('Pick at least one surface for the QR'); return; }
    setSaving(true);
    try {
      const r = await axios.put(`${API}/ads/${ad.id}/qr`, { qrEnabled: enabled, qrUrl: url, qrCaption: caption, qrPlacements: placements }, { headers });
      setCurrent(r.data);
      if (r.data.qrStatus === 'verified') toast.success('Link verified and QR is live');
      else if (r.data.qrStatus === 'flagged') toast.error('Link flagged by the safety check — it will not be shown');
      else toast.warning('Link saved, but the safety check needs another look');
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save QR'); }
    setSaving(false);
  };

  const recheck = async () => {
    setSaving(true);
    try {
      const r = await axios.post(`${API}/ads/${ad.id}/qr/recheck`, {}, { headers });
      setCurrent(c => ({ ...c, ...r.data }));
      toast.success(`Re-checked: ${r.data.qrStatus}`);
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Re-check failed'); }
    setSaving(false);
  };

  const setOverride = async (force) => {
    setSaving(true);
    try {
      const r = await axios.post(`${API}/ads/${ad.id}/qr/override`, { force }, { headers });
      setCurrent(c => ({ ...c, qrOverride: r.data.qrOverride }));
      toast.success(force ? 'Override on — this QR will go live on stream' : 'Override removed');
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Override failed'); }
    setSaving(false);
  };

  const disable = async () => {    try {
      await axios.delete(`${API}/ads/${ad.id}/qr`, { headers });
      setEnabled(false);
      toast.success('QR disabled — it will stop appearing on stream');
      onSaved?.();
    } catch { toast.error('Failed'); }
  };

  const verdict = VERDICTS[current?.qrStatus] || null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#0d0d0d] border-white/10 max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white font-heading flex items-center gap-2"><QrCode className="w-4 h-4 text-[#FF3B30]" /> Scan-to-Click — {ad?.filename}</DialogTitle>
          <DialogDescription className="text-gray-500">Every scan is routed through AYO, so you get scan counts broken down by creator.</DialogDescription>
        </DialogHeader>

        <div className="grid md:grid-cols-[1fr_240px] gap-6">
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-mono text-gray-500 uppercase tracking-widest">Destination URL</Label>
              <Input data-testid="qr-url-input" value={url} onChange={e => setUrl(e.target.value)} placeholder="brand.com/offer" className="bg-black/40 border-white/10 text-white mt-1" />
            </div>
            <div>
              <Label className="text-xs font-mono text-gray-500 uppercase tracking-widest">Call to Action <span className="text-gray-600">shown under the code</span></Label>
              <Input data-testid="qr-caption-input" value={caption} maxLength={60} onChange={e => setCaption(e.target.value)} placeholder="SCAN FOR 30% OFF" className="bg-black/40 border-white/10 text-white mt-1" />
            </div>
            <div>
              <Label className="text-xs font-mono text-gray-500 uppercase tracking-widest">Where it appears</Label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {SURFACES.map(({ id, label, hint, icon: Icon }) => {
                  const on = placements.includes(id);
                  return (
                    <button key={id} data-testid={`qr-surface-${id}`} onClick={() => togglePlacement(id)}
                      className={`p-3 rounded-lg border text-left transition-all ${on ? 'border-[#FF3B30]/50 bg-[#FF3B30]/[0.07]' : 'border-white/10 bg-white/[0.02] hover:border-white/20'}`}>
                      <Icon className={`w-4 h-4 mb-1.5 ${on ? 'text-[#FF3B30]' : 'text-gray-500'}`} />
                      <p className="text-[11px] font-bold text-white leading-tight">{label}</p>
                      <p className="text-[9px] text-gray-600 font-mono mt-0.5">{hint}</p>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-white/[0.07] bg-white/[0.02] px-3 py-2.5">
              <div>
                <p className="text-[11px] font-mono text-gray-300 uppercase tracking-widest">QR live on stream</p>
                <p className="text-[9px] text-gray-600 font-mono">Turn off to keep the link but hide the code</p>
              </div>
              <Switch data-testid="qr-enabled-switch" checked={enabled} onCheckedChange={setEnabled} />
            </div>

            {verdict && (
              <div data-testid="qr-verdict" className={`rounded-lg border px-3 py-3 ${verdict.cls}`}>
                <div className="flex items-center gap-2">
                  <verdict.Icon className="w-4 h-4" />
                  <span className="text-xs font-mono uppercase tracking-widest font-bold">Safety check: {verdict.label}</span>
                  {current?.qrCheck?.confidence ? <span className="text-[10px] font-mono opacity-70">{current.qrCheck.confidence}% confidence</span> : null}
                  <button data-testid="qr-recheck" onClick={recheck} disabled={saving} className="ml-auto text-[10px] font-mono uppercase tracking-wider opacity-70 hover:opacity-100 flex items-center gap-1"><RefreshCw className={`w-3 h-3 ${saving ? 'animate-spin' : ''}`} /> Re-check</button>
                </div>
                {current?.qrCheck?.reason && <p className="text-[11px] mt-1.5 opacity-90">{current.qrCheck.reason}</p>}
                {current?.qrCheck?.pageTitle && <p className="text-[10px] font-mono mt-1 opacity-50 truncate">{current.qrCheck.pageTitle}</p>}
                {isAdmin && (current?.qrOverride || ['flagged', 'unreachable'].includes(current?.qrStatus)) && (
                  <div className="mt-2.5 pt-2.5 border-t border-white/10 flex items-center gap-2">
                    {current?.qrOverride ? (
                      <>
                        <span className="text-[10px] font-mono uppercase tracking-wider text-emerald-400">Override active — QR is live</span>
                        <button data-testid="qr-override-off" onClick={() => setOverride(false)} disabled={saving}
                          className="ml-auto text-[10px] font-mono uppercase tracking-wider text-gray-400 hover:text-white">Remove override</button>
                      </>
                    ) : (
                      <>
                        <span className="text-[10px] font-mono text-gray-400">Admin: push it live anyway for testing.</span>
                        <button data-testid="qr-override-on" onClick={() => setOverride(true)} disabled={saving}
                          className="ml-auto text-[10px] font-mono uppercase tracking-wider text-[#FF3B30] hover:text-white">Force live</button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {trackedUrl && (
              <div className="flex items-center gap-2">
                <Link2 className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                <code className="bg-black/40 border border-white/10 rounded px-2 py-1.5 text-[11px] font-mono text-emerald-400 truncate flex-1">{trackedUrl}</code>
                <Button size="sm" variant="ghost" className="text-gray-500 hover:text-white h-7 px-2 border border-white/10" onClick={() => { navigator.clipboard.writeText(trackedUrl); toast.success('Tracked link copied'); }}><Copy className="w-3 h-3" /></Button>
              </div>
            )}

            <div className="flex items-center gap-2 pt-1">
              <Button data-testid="qr-save-button" onClick={save} disabled={saving} className="bg-[#FF3B30] hover:bg-[#D70015] text-white font-bold flex-1">{saving ? 'Checking link…' : current ? 'Save & Re-verify' : 'Generate QR & Verify Link'}</Button>
              {current && <Button variant="ghost" onClick={disable} className="text-gray-400 hover:text-red-400 border border-white/10">Disable</Button>}
            </div>
          </div>

          <div className="space-y-4">
            <div className="rounded-xl border border-white/[0.07] bg-black/50 p-4 flex flex-col items-center">
              <div className="bg-white rounded-lg p-2.5">
                <QRCodeSVG value={previewValue} size={150} level="M" includeMargin={false} />
              </div>
              {caption ? (
                <div className="mt-3 px-3 py-1 rounded-full bg-[#FF3B30] text-white text-[10px] font-mono font-bold uppercase tracking-wider text-center">{caption}</div>
              ) : (
                <div className="mt-3 text-[10px] font-mono text-gray-600 uppercase tracking-widest">Overlay preview</div>
              )}
              <p className="text-[9px] font-mono text-gray-600 mt-2 text-center">{trackedUrl ? 'Tracked link — scans attributed per creator' : 'Save to generate the tracked link'}</p>
            </div>

            <div className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-4">
              <div className="flex items-center gap-2 mb-2"><Scan className="w-3.5 h-3.5 text-[#FF3B30]" /><span className="text-[10px] font-mono text-gray-400 uppercase tracking-widest">Scans</span></div>
              <p data-testid="qr-total-scans" className="text-3xl font-mono font-black text-white leading-none">{stats?.totalScans ?? 0}</p>
              {stats?.byCreator?.length ? (
                <div className="mt-3 space-y-1">
                  {stats.byCreator.slice(0, 6).map(c => (
                    <div key={c.name} className="flex items-center justify-between text-[10px] font-mono">
                      <span className="text-gray-400 truncate">{c.name}</span>
                      <span className="text-white">{c.scans}</span>
                    </div>
                  ))}
                </div>
              ) : <p className="text-[10px] font-mono text-gray-600 mt-2">No scans yet</p>}
              {stats?.bySurface?.length ? (
                <div className="mt-3 pt-2 border-t border-white/5 space-y-1">
                  {stats.bySurface.map(s => (
                    <div key={s.surface} className="flex items-center justify-between text-[10px] font-mono">
                      <span className="text-gray-600 uppercase">{s.surface}</span>
                      <span className="text-gray-400">{s.scans}</span>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
